import React, { useEffect, useState } from "react";
import FormField from "../components/FormField";
import PlateField from "../components/PlateField";
import { SectionCard } from "../components/panel/PanelUI";
import { getCurrentUser } from "../api/authApi";
import { createVehicleInspection } from "../api/vehicleInspectionApi";
import {
  CAR_PANELS,
  PANEL_STATUS_OPTIONS,
  INITIAL_INSPECTION_DATA,
  buildVehicleInspectionPayload,
} from "../utils/vehicleInspectionSubmission";

// آیکن ساده‌ی دایره‌ای، هم‌راستا با استایل برگه‌ی «قرارداد۲۴»
function FieldIcon({ children }) {
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-navy text-base text-white">
      {children}
    </div>
  );
}

export default function VehicleInspectionForm({ onSuccess, onBack }) {
  const [data, setData] = useState(INITIAL_INSPECTION_DATA);
  const [inspectorName, setInspectorName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    getCurrentUser()
      .then((user) => {
        if (active) setInspectorName(user?.name || "");
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePanelChange = (panelKey, value) => {
    setData((prev) => ({
      ...prev,
      carPanels: { ...prev.carPanels, [panelKey]: value },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!data.customerPhone) {
      setError("لطفاً شماره تلفن مشتری را وارد کنید.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildVehicleInspectionPayload(data);
      const created = await createVehicleInspection(payload);
      setData(INITIAL_INSPECTION_DATA);
      onSuccess?.(created);
    } catch (err) {
      setError(err.message || "خطا در ثبت برگه بازرسی. دوباره تلاش کنید.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface px-4 py-6 sm:py-10">
      <div className="mx-auto w-full max-w-3xl">
        {/* هدر */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy text-lg text-white">
              🔍
            </div>
            <div>
              <h1 className="text-base font-extrabold text-navy sm:text-lg">
                برگه بازرسی خودرو
              </h1>
              <p className="text-xs text-gray-500">قرارداد ۲۴</p>
            </div>
          </div>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="text-xs font-bold text-gray-400 hover:text-navy"
            >
              بازگشت
            </button>
          )}
        </div>

        {error && (
          <div className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-bold text-red-600">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* مشتری */}
          <SectionCard title="اطلاعات مشتری">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                label="نام مالک خودرو / مشتری"
                name="vehicleOwner"
                value={data.vehicleOwner}
                onChange={handleChange}
                placeholder="نام و نام خانوادگی"
                required
              />
              <FormField
                label="تلفن مشتری"
                name="customerPhone"
                value={data.customerPhone}
                onChange={handleChange}
                placeholder="09xxxxxxxxx"
                required
              />
            </div>
          </SectionCard>

          {/* اطلاعات خودرو */}
          <SectionCard title="اطلاعات خودرو">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                label="نوع خودرو"
                name="vehicleName"
                value={data.vehicleName}
                onChange={handleChange}
                placeholder="مثلاً پژو ۲۰۷"
                required
              />
              <FormField
                label="رنگ خودرو"
                name="vehicleColor"
                value={data.vehicleColor}
                onChange={handleChange}
                placeholder="مثلاً سفید"
                required
              />
              <FormField
                label="مدل"
                name="vehicleModel"
                value={data.vehicleModel}
                onChange={handleChange}
                placeholder="مثلاً هاچبک"
              />
              <FormField
                label="سال ساخت"
                name="vehicleYear"
                type="number"
                value={data.vehicleYear}
                onChange={handleChange}
                placeholder="۱۴۰۲"
                required
              />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-navy-light">
                  شماره پلاک <span className="text-red-500">*</span>
                </label>
                <PlateField value={data.plateNumber} onChange={handleChange} />
              </div>
              <FormField
                label="شماره موتور"
                name="engineNumber"
                value={data.engineNumber}
                onChange={handleChange}
                placeholder="شماره موتور خودرو"
              />
              <FormField
                label="شماره شاسی"
                name="vin"
                value={data.vin}
                onChange={handleChange}
                placeholder="شماره شاسی خودرو"
                full
                required
              />
            </div>
          </SectionCard>

          {/* وضعیت بدنه */}
          <SectionCard title="وضعیت بدنه">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {CAR_PANELS.map((panel) => (
                <div key={panel.key} className="flex items-center gap-2">
                  <FieldIcon>🚘</FieldIcon>
                  <div className="flex-1">
                    <label className="mb-1 block text-xs font-medium text-navy-light">
                      {panel.label}
                    </label>
                    <select
                      value={data.carPanels[panel.key]}
                      onChange={(e) =>
                        handlePanelChange(panel.key, e.target.value)
                      }
                      className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy outline-none transition focus:border-sky focus:ring-2 focus:ring-sky-light/50"
                    >
                      {PANEL_STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* اطلاعات کارشناس */}
          <SectionCard title="اطلاعات کارشناس">
            <div className="rounded-xl border border-gray-200 bg-surface px-4 py-3 text-sm text-navy-light">
              کارشناس بازرسی:{" "}
              <span className="font-bold text-navy">
                {inspectorName || "—"}
              </span>
            </div>
          </SectionCard>

          {/* توضیحات */}
          <SectionCard title="توضیحات">
            <div className="space-y-4">
              <p className="text-xs text-gray-400">
                گزارش بازرسی برای همان شماره مشتری («
                {data.customerPhone || "..."}
                ») ارسال می‌شود.
              </p>
              <FormField
                label="توضیحات"
                name="description"
                type="textarea"
                rows={4}
                value={data.description}
                onChange={handleChange}
                placeholder="توضیحات تکمیلی درباره وضعیت خودرو..."
                full
              />
            </div>
          </SectionCard>

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-xl bg-navy px-5 py-3 text-sm font-bold text-white transition hover:bg-navy-light disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "در حال ثبت..." : "ثبت برگه بازرسی"}
          </button>
        </form>

        <p className="mt-4 text-center text-xs text-gray-400">
          مدت زمان اعتبار این برگه ۲۴ ساعت می‌باشد.
        </p>
      </div>
    </div>
  );
}
